/* eslint-disable */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Message } from '@meitu/saas-ui';
import { getRequestIdentityHeader } from '@meitu/xdesign-identity';
import { getLocaleStore } from '../i18n';
import { sendTimingLog } from './utils';

// 提示服务端错误信息，错误码 > 60000000
const BUSINESS_ERROR_CODE_BASE = 60000000;

export interface RequestConfig extends AxiosRequestConfig {
    // 默认展示错误提示
    hideErrorToast?: boolean;
    // 隐藏未登录提示
    hideUnauthorizedToast?: boolean;
    // 自定义数据传递
    metadata?: {
        startTime?: number;
    };
}

export interface RequestInterface {
    <T>(url: string, options: RequestConfig): Promise<AxiosResponse<T>>;
}

// 接口返回数据结构定义
export interface ResponseInStructure {
    // 状态码，0-成功，非0-错误
    error_code?: number;
    // 错误信息
    msg?: string;
    // 数据
    data?: unknown;
}

export interface InitOptionsConfig extends AxiosRequestConfig {
    config?: {
        adaptor: (responseData: unknown) => ResponseInStructure;
        // 成功状态code
        successCode?: number;
        // 未登录态code
        unauthorizedCodes: number[];
        // 全局请求异常提示信息
        errorMessage?: string;
        // 不要强制登录的接口列表
        unLoginHandlerList?: string[];
        // 不强制登录的路径
        unLoginPath?: string[];
        // 不需要强制登录的域名
        unLoginHost?: string[];
        // 是否启用请求耗时埋点
        enableTimingLog?: boolean;
        // 全局请求异常处理函数
        errorHandler?: (error: AxiosError) => void;
        // 未登录/登录态失效处理函数
        loginHandler?: () => Promise<string>;
    };
    // request 拦截器
    requestInterceptors?: unknown[];
    // response 拦截器
    responseInterceptors?: unknown[];
}

let __instance: AxiosInstance;
let requestConfig: InitOptionsConfig;
let loginInstance: unknown = null;

/**
 *  配置全局默认参数
 */
export const extend = (initOptions: InitOptionsConfig) => {
    requestConfig = initOptions;
};

/**
 * 创建axios实例
 */
export const getInstance = (clone = false) => {
    const { $t } = getLocaleStore();
    if (__instance && !clone) {
        return __instance;
    }

    const instance = axios.create({
        ...requestConfig,
    });

    if (!clone) {
        __instance = instance;
    }

    instance.interceptors.response.use(
        async (response) => {
            const config: RequestConfig = response.config;

            const hideErrorToast = config.hideErrorToast;
            const hideUnauthorizedToast = config.hideUnauthorizedToast;

            const adaptor = requestConfig?.config?.adaptor;
            let data = response.data || {};
            data = adaptor ? adaptor(data) : data;

            const UNAUTHORIZED = requestConfig?.config?.unauthorizedCodes || [];
            const unLoginHandlerList = requestConfig?.config?.unLoginHandlerList || [];
            const loginHandler = requestConfig?.config?.loginHandler || (() => Promise.resolve());
            const unLoginPath = requestConfig?.config?.unLoginPath || [];
            const unLoginHost = requestConfig?.config?.unLoginHost || [];
            const successCode = requestConfig?.config?.successCode || 0;

            const code = data.error_code || data.code;
            const msg = data.msg || data.message;

            // 去除域名后的接口路径
            const requestPath = config?.url?.replace(/^(http.+\.com)/, '') || '';

            const pathname = location.pathname;

            // 如果启用了埋点，计算耗时并上报
            if (requestConfig?.config?.enableTimingLog && config.metadata?.startTime) {
                const duration = performance.now() - config.metadata.startTime;
                sendTimingLog({
                    url: config.url || '',
                    method: config.method || '',
                    duration,
                    status: response.status,
                    code: response.data?.code || response.data?.error_code || 0,
                });
            }

            const disableAutoLogin =
                unLoginPath.includes(pathname) || unLoginHost.some((host) => location.host.includes(host));
            if (UNAUTHORIZED.includes(code) && !unLoginHandlerList.includes(requestPath) && !disableAutoLogin) {
                // 等待登录结束
                const token = loginInstance ? await loginInstance : await (loginInstance = loginHandler());

                // 登录成功后释放实例
                loginInstance = null;

                // 登录成功后重新发起请求
                if (token) {
                    // 设置最新的 token
                    if (response.config.headers) {
                        response.config.headers.access_token = token;
                    }
                    // 重新发起请求
                    return axios.request(response.config);
                }
            } else if (code && code !== successCode) {
                /**
                 * 隐藏通用错误提示
                 * 未登录时隐藏未登录提示, 统一提示服务端错误信息错误码 > 60000000
                 */
                if (
                    !hideErrorToast &&
                    (!(hideUnauthorizedToast && UNAUTHORIZED.includes(code)) ||
                        (code > BUSINESS_ERROR_CODE_BASE && msg))
                )
                    Message.error(msg || $t((v) => v.request.errorText));
            }

            // 对响应数据做点什么
            return response;
        },
        (error) => {
            //http状态码错误
            const errorHandler = requestConfig?.config?.errorHandler;
            const errorMessage = requestConfig?.config?.errorMessage;

            // console.log('error =>> ', error.config);

            const hideErrorToast = error.config.hideErrorToast || false;

            const res = error.response;
            const msg = errorMessage || res?.data?.msg || res?.data?.message || $t((v) => v.utils.networkErr);

            if (!hideErrorToast) {
                Message.error(msg);
            }

            if (errorHandler) {
                errorHandler(error);
            }

            const config: RequestConfig = error.config;
            if (requestConfig?.config?.enableTimingLog && config.metadata?.startTime) {
                const duration = performance.now() - config.metadata.startTime;

                sendTimingLog({
                    url: config.url || '',
                    method: config.method || '',
                    duration,
                    status: error?.response?.status || 0,
                    code: -1,
                    error: true,
                });
            }

            return Promise.reject({ msg, network: true, data: res?.data || {} });
        },
    );

    // 添加自定义拦截器
    const requestInterceptors = requestConfig?.requestInterceptors || [];
    const responseInterceptors = requestConfig?.responseInterceptors || [];

    // 添加团队id
    instance.interceptors.request.use((config: any) => {
        config.headers = {
            ...getRequestIdentityHeader(),
            ...(config.headers || {}),
        };

        // 如果开启了耗时埋点，则记录开始时间
        if (requestConfig?.config?.enableTimingLog) {
            config.metadata = { startTime: performance.now() };
        }
        return config;
    });
    requestInterceptors.map((ri: any) => {
        instance.interceptors.request.use(ri);
    });
    responseInterceptors.map((ri: any) => {
        instance.interceptors.response.use(ri);
    });

    return instance;
};

const request: RequestInterface = (url: string, options: RequestConfig) => {
    const requestInstance = getInstance();

    return requestInstance(url, options);
};

export default request;
